import { useCallback } from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Shell } from '@socialgoodsoftware/components';
import { Logo } from '@socialgoodsoftware/components';
import { MainLoader } from '@socialgoodsoftware/components';
import { OrganizationSwitchingLoader } from '@socialgoodsoftware/components';
import { SkipLink } from '@socialgoodsoftware/components';
import { useSessionBroadcast } from '@socialgoodsoftware/components';
import { HelpBeacon } from '@socialgoodsoftware/components';
import { openAiAnswers } from '@socialgoodsoftware/components';
import { useFeatures } from '@socialgoodsoftware/components';
import { defaultPreferences } from '@socialgoodsoftware/libraries/models/tables/preference-values';
import { MENU_ITEMS } from '@socialgoodsoftware/frbac';
import { PRODUCTS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ACCOUNT_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ORGANIZATION_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { TEAM_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ROLES_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { API_KEYS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ASSETS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { DOMAINS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ACTIVITY_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { RESOURCES_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { SECURITY_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { useOrganization } from '../contexts/OrganizationContext';
import { useRunAs } from '../contexts/RunAsContext';
import { RunAsBanner, runAsBannerHeight } from '../components/run-as/RunAsBanner';
import { SessionTimeoutWatcher } from '../components/session/SessionTimeoutWatcher';
import { buildLoginRedirect } from '../lib/login-redirect';
import { featureQueryKey } from '../lib/api-client';
import { preferencesApi } from '../preferences-api';

import type { ReactNode } from 'react';
import type { NavItem } from '@socialgoodsoftware/components';
import type { AppShellAccount } from '@socialgoodsoftware/components';
import type { UserMenuItem } from '@socialgoodsoftware/components';
import type { Organization } from '@socialgoodsoftware/components';

type NavConfig = {
  icon: ReactNode;
  to: string;
  match?: string;
};

const pendingMainSx = {
  '&:focus-visible': { outline: '0.125rem solid', outlineColor: 'primary.main', outlineOffset: '0.125rem' },
};

// Menu order, titles and visibility come from frbac; this map only supplies the app-local icon
// and the SPA route each entry's feature group opens.
const navConfig: Record<string, NavConfig> = {
  [SECURITY_FEATURE_GROUP.guid]: {
    icon: <ShieldOutlinedIcon aria-hidden />,
    to: '/security',
  },
  [PRODUCTS_FEATURE_GROUP.guid]: {
    icon: <DashboardOutlinedIcon aria-hidden />,
    to: '/dashboard',
  },
  [ACCOUNT_FEATURE_GROUP.guid]: {
    icon: <PersonOutlinedIcon aria-hidden />,
    to: '/profile',
  },
  [ORGANIZATION_FEATURE_GROUP.guid]: {
    icon: <BusinessOutlinedIcon aria-hidden />,
    to: '/organization',
  },
  [TEAM_FEATURE_GROUP.guid]: {
    icon: <GroupOutlinedIcon aria-hidden />,
    to: '/team',
  },
  [ROLES_FEATURE_GROUP.guid]: {
    icon: <AdminPanelSettingsOutlinedIcon aria-hidden />,
    to: '/roles/roles',
    match: '/roles',
  },
  [API_KEYS_FEATURE_GROUP.guid]: {
    icon: <CodeOutlinedIcon aria-hidden />,
    to: '/keys',
    match: '/keys',
  },
  [ASSETS_FEATURE_GROUP.guid]: {
    icon: <ImageOutlinedIcon aria-hidden />,
    to: '/assets',
  },
  [DOMAINS_FEATURE_GROUP.guid]: {
    icon: <LanguageOutlinedIcon aria-hidden />,
    to: '/domains',
  },
  [ACTIVITY_FEATURE_GROUP.guid]: {
    icon: <HistoryOutlinedIcon aria-hidden />,
    to: '/activity',
  },
  [RESOURCES_FEATURE_GROUP.guid]: {
    icon: <MenuBookOutlinedIcon aria-hidden />,
    to: '/resources',
  },
};

function AppShellContent({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const {
    organizations,
    organizationSelection,
    selectOrganization,
    loadOrganizations,
    isLoading,
    switchingToOrganization,
    user,
  } = useOrganization();
  const { isMenuGroupVisible } = useFeatures();
  const { isRunAsActive } = useRunAs();
  const preferencesQuery = preferencesApi.getPreferences.useQuery({
    queryKey: featureQueryKey('preferences'),
    enabled: Boolean(user),
    retry: false,
  });

  let fullWidth = defaultPreferences.fullWidth;
  if (preferencesQuery.data?.status === 200) {
    fullWidth = preferencesQuery.data.body.fullWidth;
  }

  // This app is the account hub, so its own pages are the hub's: the menu's account link and its
  // log out are same-origin paths rather than the absolute ones a product app is handed.
  const shellAccount = useMemo(() => {
    let value: AppShellAccount | null = null;
    if (user) {
      value = {
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email,
        profilePhotoUrl: user.profilePhotoUrl ?? null,
        accountHubBaseUrl: '',
        accountHubLogoutUrl: '/logout',
      };
    }
    return value;
  }, [user]);

  useSessionBroadcast({
    onLogout: () => {
      queryClient.clear();
      navigate(buildLoginRedirect(location.pathname, location.search), { replace: true });
    },
  });

  const handleNavClick = useCallback(() => {
    void queryClient.invalidateQueries();
  }, [queryClient]);

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];
    for (const entry of MENU_ITEMS) {
      const config = navConfig[entry.groupGuid];
      if (config !== undefined && isMenuGroupVisible(entry.groupGuid)) {
        items.push({
          label: entry.title,
          icon: config.icon,
          to: config.to,
          spa: true,
          match: config.match ?? config.to,
          onClick: handleNavClick,
        });
      }
    }
    return items;
  }, [isMenuGroupVisible, handleNavClick]);

  // The account hub's own pages are the menu's one dynamic group; every other app supplies its
  // own, and the log out under them belongs to the shell rather than to any app.
  const userMenuGroups = useMemo<UserMenuItem[][]>(() => {
    return [navItems.map((item) => ({ label: item.label, href: item.to }))];
  }, [navItems]);

  const handleOrganizationChange = (selection: Organization) => {
    selectOrganization(selection);
  };

  if (switchingToOrganization !== null) {
    return <OrganizationSwitchingLoader organization={switchingToOrganization} />;
  }

  if (isLoading) {
    return (
      <>
        <SkipLink />
        <Box component="main" id="main" tabIndex={-1} sx={pendingMainSx}>
          <MainLoader loaderText="Loading account" />
        </Box>
      </>
    );
  }

  return (
    <>
      <SkipLink />
      {isRunAsActive ? (
        <GlobalStyles styles={{ '.MuiAppBar-root.MuiAppBar-positionFixed': { top: `${runAsBannerHeight}px` } }} />
      ) : null}
      <RunAsBanner />
      <Box>
        <Shell
          topOffset={isRunAsActive ? runAsBannerHeight : 0}
          logo={<Logo />}
          navVariant="sidebar"
          navItems={navItems}
          collapsed
          account={shellAccount}
          userMenuGroups={userMenuGroups}
          logoutDisabled={isRunAsActive}
          linkComponent={Link}
          currentPath={location.pathname}
          organizations={organizations}
          selectedOrganization={organizationSelection}
          onOrganizationChange={handleOrganizationChange}
          onOrganizationsMenuOpen={loadOrganizations}
          onAiAssistantClick={openAiAnswers}
          product="account"
          fluid={fullWidth}
        >
          {children}
        </Shell>
        <SessionTimeoutWatcher />
        <HelpBeacon />
      </Box>
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  return <AppShellContent>{children}</AppShellContent>;
}
